import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';
import { Euro, AlertTriangle, Info } from 'lucide-react';

/**
 * LiveRateDisplay - Zeigt den aktuell gültigen Satz für eine Projektzeiterfassung
 * 
 * Props:
 * - projectId: Projekt-ID
 * - date: Datum (YYYY-MM-DD)
 * - activityType: Optionale Tätigkeitsart
 * - rateType: 'hourly' | 'daily'
 */
export default function LiveRateDisplay({
  projectId,
  date,
  activityType,
  rateType = 'hourly',
}) {
  const { api } = useAuth();
  const [rateInfo, setRateInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return format(parseISO(dateStr), 'dd.MM.yyyy', { locale: de });
    } catch {
      return dateStr;
    }
  };

  // Source label
  const getSourceLabel = (source) => {
    switch (source) {
      case 'activity': return 'Tätigkeitssatz';
      case 'project': return 'Projektsatz';
      case 'customer': return 'Kundensatz';
      case 'legacy_project': return 'Projekt-Fallback';
      case 'none': return 'Kein Satz';
      default: return source;
    }
  };

  useEffect(() => {
    if (!projectId || !date) {
      setRateInfo(null);
      return;
    }

    const fetchRate = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        const params = new URLSearchParams({
          project_id: projectId,
          date: date,
          rate_type: rateType,
        });
        if (activityType) {
          params.append('activity_type', activityType);
        }
        
        const response = await api.get(`/rates/resolve?${params.toString()}`);
        setRateInfo(response.data);
      } catch (err) {
        console.error('Fehler beim Laden des Satzes:', err);
        // Don't show error for 403 (user may not have permission)
        if (err.response?.status !== 403) {
          setError('Satz konnte nicht ermittelt werden');
        }
        setRateInfo(null);
      } finally {
        setIsLoading(false);
      }
    };

    // Debounce the fetch
    const timer = setTimeout(fetchRate, 300);
    return () => clearTimeout(timer);
  }, [api, projectId, date, activityType, rateType]);

  if (!projectId || !date) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="live-rate-loading">
        <Euro className="h-3 w-3 animate-pulse" />
        Satz wird ermittelt...
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="live-rate-error">
        <Info className="h-3 w-3" />
        {error}
      </div>
    );
  }

  if (!rateInfo) {
    return null;
  }

  // Missing rate warning
  if (rateInfo.missing) {
    return (
      <div
        className="flex items-center gap-2 text-xs text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded px-2 py-1"
        data-testid="live-rate-missing"
      >
        <AlertTriangle className="h-3 w-3" />
        Kein Satz hinterlegt für dieses Datum
      </div>
    );
  }

  // Valid rate display
  return (
    <div
      className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/50 rounded px-2 py-1"
      data-testid="live-rate-display"
    >
      <Euro className="h-3 w-3 text-emerald-600 dark:text-emerald-400" />
      <span className="font-medium text-foreground">
        {formatCurrency(rateInfo.amount)}
      </span>
      <span>/{rateType === 'hourly' ? 'h' : 'Tag'}</span>
      <span className="text-muted-foreground">
        ({getSourceLabel(rateInfo.source)}
        {rateInfo.valid_from && `, ab ${formatDate(rateInfo.valid_from)}`})
      </span>
    </div>
  );
}
